import { useQueryClient } from '@tanstack/react-query'
import { Check, Copy, ExternalLink, Globe, RefreshCw, Star, Trash2 } from 'lucide-react'
import { useState, type FormEvent, type ReactNode } from 'react'
import { Link, useOutletContext } from 'react-router'

import { type DominioDeLaWeb, type DominiosDeLaWeb, type EstadoDeDominio, useDominios } from '../api/dominios'
import { ErrorDeLaApi, pedir } from '../api/nucleo'
import type { Sitio } from '../api/tipos'
import { Escribenos } from '../componentes/Ayuda'
import { Aviso, Boton, Casilla, Esqueleto, Modal, Pastilla, Tarjeta, Titulo, claseDeEntrada, clases } from '../componentes/ui'
import { direccionDeLaWeb } from '../marco/secciones'
import type { Contexto } from './Modulo'

/**
 * «Dominio» de una web (CLAUDE.md §4, «Conectar un dominio propio»): la dirección que
 * tiene siempre (`<slug>.wixy.app`), los dominios propios con **su estado comprobado
 * por el núcleo** —esperando el DNS, lo estamos conectando, el certificado se está
 * emitiendo, conectado, o qué problema hay— y lo que hay que poner exactamente en el
 * proveedor del dominio. Añadir, quitar y elegir el principal van por la API
 * (`/api/panel/dominios`), con las reglas de siempre: nada de lo de la plataforma, y un
 * dominio no puede ser de dos webs.
 */
export const Dominio = () => {
  const { espacio, permisos, sitio } = useOutletContext<Contexto>()
  if (!sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  if (permisos.cargando) return <Esqueleto />
  // Conectar un dominio es editar la web (el núcleo pide `ajustes.editar` en `/api/panel/dominios`).
  if (!permisos.tiene('ajustes.editar')) {
    return (
      <>
        <Titulo rotulo={sitio.nombre}>Dominio</Titulo>
        <Aviso tono="info">Los dominios de la web los gestiona quien administra la cuenta. Si hace falta cambiar algo, pídeselo.</Aviso>
      </>
    )
  }
  return <Pantalla equipo={permisos.esEquipo} espacio={espacio} key={sitio.id} sitio={sitio} />
}

const ESTADO: Record<EstadoDeDominio, { clave: 'construyendo' | 'fallida' | 'publicado' | 'sin-publicar'; texto: string }> = {
  'esperando-dns': { clave: 'sin-publicar', texto: 'Esperando el DNS' },
  conectando: { clave: 'construyendo', texto: 'Lo estamos conectando' },
  certificado: { clave: 'construyendo', texto: 'El certificado se está emitiendo' },
  conectado: { clave: 'publicado', texto: 'Conectado' },
  problema: { clave: 'fallida', texto: 'Hay un problema' },
}

const hace = (iso: null | string): string => {
  if (!iso) return ''
  const minutos = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (minutos < 1) return 'hace un momento'
  if (minutos < 60) return `hace ${minutos} min`
  const horas = Math.round(minutos / 60)
  return horas < 24 ? `hace ${horas} h` : new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short' })
}

const Pantalla = ({ espacio, equipo, sitio }: { equipo: boolean; espacio: number; sitio: Sitio }) => {
  const clienteDeConsultas = useQueryClient()
  const { data, error, isPending } = useDominios(sitio.id)
  // El aviso sale donde se ha pulsado: el de «Añadir», junto al formulario de abajo; el
  // resto, encima de la lista. Arriba del todo no lo vería quien acaba de pulsar abajo.
  const [aviso, setAviso] = useState<null | { donde: 'anadir' | 'lista' | 'redirigir'; texto: string; tono: 'error' | 'ok' }>(null)
  const [ocupado, setOcupado] = useState<null | string>(null)
  const [quitando, setQuitando] = useState<null | string>(null)

  const hacer = async (clave: string, ruta: string, cuerpo: Record<string, unknown>, bien?: string) => {
    const donde = clave === 'anadir' ? 'anadir' : clave === 'redirigir' ? 'redirigir' : 'lista'
    setOcupado(clave)
    setAviso(null)
    try {
      const nuevo = await pedir<DominiosDeLaWeb>(ruta, { metodo: 'POST', cuerpo: { sitio: sitio.id, ...cuerpo } })
      clienteDeConsultas.setQueryData(['dominios', sitio.id], nuevo)
      // La lista de webs enseña el dominio principal: que se entere.
      void clienteDeConsultas.invalidateQueries({ queryKey: ['sitios', espacio] })
      if (bien) setAviso({ donde, tono: 'ok', texto: bien })
      return true
    } catch (e) {
      setAviso({ donde, tono: 'error', texto: e instanceof ErrorDeLaApi ? e.message : 'No se ha podido hacer. Vuelve a intentarlo en un momento.' })
      return false
    } finally {
      setOcupado(null)
    }
  }
  const avisoEn = (donde: 'anadir' | 'lista' | 'redirigir') =>
    aviso?.donde === donde ? (
      <div className="mb-4">
        <Aviso tono={aviso.tono}>{aviso.texto}</Aviso>
      </div>
    ) : null

  const sinPublicar = !sitio.versionPublicada

  return (
    <>
      <Titulo
        descripcion="La dirección en la que se ve tu web. Siempre tiene una propia; si tienes un dominio tuyo, conéctalo aquí."
        rotulo={sitio.nombre}
        volver={
          <Link className="mb-2 inline-block text-[13px] text-tenue hover:text-tinta" to={`/e/${espacio}`}>
            ← Mis sitios web
          </Link>
        }
      >
        Dominio
      </Titulo>

      {isPending ? (
        <Tarjeta sinRelleno>
          <Esqueleto filas={4} />
        </Tarjeta>
      ) : error || !data ? (
        <Aviso tono="error">{error instanceof ErrorDeLaApi ? error.message : 'No hemos podido leer los dominios de esta web. Vuelve a intentarlo en un momento.'}</Aviso>
      ) : (
        <div className="flex flex-col gap-5">
          <Tarjeta>
            <p className="mb-1 text-xs font-medium tracking-wide text-tenue uppercase">Tu dirección fija</p>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-display text-lg font-semibold break-all">{data.direccionFija}</p>
              {!sinPublicar && (
                <a href={direccionDeLaWeb(data.direccionFija) ?? undefined} rel="noreferrer" target="_blank">
                  <Boton icono={<ExternalLink />} pequeno tipo="discreto">
                    Abrir
                  </Boton>
                </a>
              )}
            </div>
            <p className="mt-1 text-[13px] text-tenue">Funciona siempre, tengas o no un dominio propio. Si conectas uno, esta sigue funcionando.</p>
          </Tarjeta>

          {sinPublicar && (
            <Aviso tono="info">
              Tu web todavía no está publicada. Puedes conectar el dominio ya, pero no enseñará nada hasta que publiques el diseño desde «Diseño».
            </Aviso>
          )}

          <Tarjeta>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-base font-semibold">Tus dominios</h2>
              {data.dominios.length > 0 && (
                <Boton cargando={ocupado === 'comprobar'} icono={<RefreshCw />} onClick={() => void hacer('comprobar', '/panel/dominios/comprobar', {})} pequeno>
                  Comprobar ahora
                </Boton>
              )}
            </div>
            {avisoEn('lista')}
            {data.dominios.length === 0 ? (
              <p className="text-sm text-tenue">Todavía no has conectado ningún dominio propio. Añádelo abajo: por ejemplo «www.tunegocio.com».</p>
            ) : (
              <ul className="flex flex-col divide-y divide-linea">
                {data.dominios.map((d) => (
                  <FilaDeDominio
                    automatico={data.automatico}
                    d={d}
                    enLista={data.dominios.map((x) => x.dominio)}
                    equipo={equipo}
                    key={d.dominio}
                    marcar={() => void hacer(`marcar-${d.dominio}`, '/panel/dominios/marcar', { dominio: d.dominio }, `${d.dominio} marcado como conectado.`)}
                    ocupado={ocupado}
                    principal={() => void hacer(`principal-${d.dominio}`, '/panel/dominios/principal', { dominio: d.dominio }, `${d.dominio} es ahora el dominio principal.`)}
                    quitar={() => setQuitando(d.dominio)}
                    anadirWww={(www) => void hacer(`www-${d.dominio}`, '/panel/dominios', { dominio: www }, `${www} añadido.`)}
                  />
                ))}
              </ul>
            )}
          </Tarjeta>

          {data.redirigir && data.dominios.length > 0 && (
            <Tarjeta>
              <label className="flex items-start gap-3">
                <Casilla
                  disabled={ocupado === 'redirigir'}
                  marcada={data.redirigir.activo}
                  onChange={(e) => void hacer('redirigir', '/panel/dominios/redirigir', { activo: e.target.checked }, e.target.checked ? 'Las demás direcciones llevarán a la principal.' : 'Cada dirección se sirve por su cuenta.')}
                />
                <span className="flex flex-col gap-1">
                  <span className="font-medium">Llevar las demás direcciones a la principal</span>
                  <span className="text-sm text-tenue">
                    Quien entre por {data.direccionFija} o por otro de tus dominios llega a <strong>{data.redirigir.principal}</strong> con una redirección permanente: Google ve una sola web y no la reparte entre varias.{' '}
                    {data.redirigir.activo && !data.redirigir.enMarcha ? 'Empezará cuando tu dominio principal esté conectado; hasta entonces responden todas.' : ''}
                  </span>
                </span>
              </label>
              {avisoEn('redirigir')}
            </Tarjeta>
          )}

          <NuevoDominio
            anadir={(dominio, conWww) => hacer('anadir', '/panel/dominios', { dominio, conWww }, 'Dominio añadido. Ahora crea el registro en tu proveedor como se explica arriba.')}
            aviso={avisoEn('anadir')}
            ocupado={ocupado === 'anadir'}
          />

          <p className="text-[13px] text-tenue">
            ¿Te has atascado con el DNS? <Escribenos contexto="dominio">Escríbenos</Escribenos> con el nombre de tu proveedor y te guiamos.
          </p>
        </div>
      )}

      {quitando && (
        <Modal
          ancho="max-w-md"
          cerrar={() => setQuitando(null)}
          pie={
            <>
              <Boton onClick={() => setQuitando(null)}>Cancelar</Boton>
              <Boton
                cargando={ocupado === `quitar-${quitando}`}
                icono={<Trash2 />}
                onClick={() => void hacer(`quitar-${quitando}`, '/panel/dominios/quitar', { dominio: quitando }, `${quitando} quitado.`).then(() => setQuitando(null))}
                tipo="peligro"
              >
                Quitar el dominio
              </Boton>
            </>
          }
          titulo="¿Quitar este dominio?"
        >
          <p className="text-sm">
            <strong>{quitando}</strong> dejará de llevar a tu web. Quien lo tenga guardado o lo encuentre en Google verá un error hasta que lo apuntes a otro sitio. Tu dirección fija sigue funcionando.
          </p>
        </Modal>
      )}
    </>
  )
}

const QUE_PASA: Record<EstadoDeDominio, (automatico: boolean) => string> = {
  'esperando-dns': () => 'Crea el registro de abajo en tu proveedor del dominio.',
  conectando: (automatico) =>
    automatico
      ? 'Tu dominio ya apunta a tu web y lo estamos dando de alta en nuestra red. Suele tardar unos minutos.'
      : 'Tu dominio ya apunta a tu web y estamos terminando de conectarlo. No tienes que hacer nada más.',
  certificado: () => 'Tu dominio ya llega a tu web. Falta el certificado de seguridad (el candado del navegador): suele tardar unos minutos, a veces un par de horas.',
  conectado: () => 'Todo listo: tu web se ve en este dominio, con su certificado de seguridad.',
  problema: () => '',
}

const FilaDeDominio = ({
  d,
  automatico,
  equipo,
  enLista,
  ocupado,
  principal,
  quitar,
  marcar,
  anadirWww,
}: {
  anadirWww: (www: string) => void
  automatico: boolean
  d: DominioDeLaWeb
  enLista: string[]
  equipo: boolean
  marcar: () => void
  ocupado: null | string
  principal: () => void
  quitar: () => void
}) => {
  const estado = ESTADO[d.estado]
  const conectado = d.estado === 'conectado'
  const [verPasos, setVerPasos] = useState(!conectado)
  return (
    <li className="py-4 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2">
            <span className="font-medium break-all">{d.dominio}</span>
            {d.principal && (
              <Pastilla clave="violeta" punto={false}>
                Principal
              </Pastilla>
            )}
            <Pastilla clave={estado.clave}>{estado.texto}</Pastilla>
          </p>
          <p className="mt-1 text-[13px] text-tinta-2">
            {d.estado === 'problema' ? (d.problema ?? 'Algo no va bien con este dominio.') : d.estado === 'esperando-dns' && d.problema ? d.problema : QUE_PASA[d.estado](automatico)}
          </p>
          {d.estado === 'esperando-dns' && <p className="mt-1 text-[13px] text-tenue">Un cambio en el DNS tarda desde unos minutos hasta 24 horas en verse; lo comprobamos solos.</p>}
          {d.comprobadoEn && <p className="mt-1 text-xs text-tenue">Comprobado {hace(d.comprobadoEn)}.</p>}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {conectado && (
            <a href={`https://${d.dominio}`} rel="noreferrer" target="_blank">
              <Boton icono={<ExternalLink />} pequeno tipo="discreto">
                Abrir
              </Boton>
            </a>
          )}
          {!d.principal && (
            <Boton cargando={ocupado === `principal-${d.dominio}`} icono={<Star />} onClick={principal} pequeno tipo="discreto">
              Hacer principal
            </Boton>
          )}
          {equipo && !automatico && !conectado && (
            <Boton cargando={ocupado === `marcar-${d.dominio}`} icono={<Check />} onClick={marcar} pequeno>
              Ya está conectado
            </Boton>
          )}
          <Boton icono={<Trash2 />} onClick={quitar} pequeno tipo="peligro">
            Quitar
          </Boton>
        </div>
      </div>

      {conectado && (
        <button className="mt-2 text-[13px] text-tenue underline-offset-2 hover:underline" onClick={() => setVerPasos((v) => !v)} type="button">
          {verPasos ? 'Ocultar el registro DNS' : 'Ver el registro DNS'}
        </button>
      )}
      {verPasos && <Pasos anadirWww={anadirWww} d={d} enLista={enLista} ocupado={ocupado} />}
    </li>
  )
}

const Copiar = ({ texto }: { texto: string }) => {
  const [hecho, setHecho] = useState(false)
  return (
    <button
      aria-label={`Copiar «${texto}»`}
      className="inline-flex size-7 items-center justify-center rounded-boton text-tenue hover:bg-papel-3 hover:text-tinta"
      onClick={() => {
        void navigator.clipboard?.writeText(texto).then(() => {
          setHecho(true)
          window.setTimeout(() => setHecho(false), 1500)
        })
      }}
      title="Copiar"
      type="button"
    >
      {hecho ? <Check className="size-4 text-ok" /> : <Copy className="size-4" />}
    </button>
  )
}

const Registro = ({ tipo, nombre, valor }: { nombre: string; tipo: string; valor: string }) => (
  <div className="mt-2 overflow-x-auto rounded-campo border border-linea">
    <table className="w-full text-left text-[13px]">
      <thead className="bg-papel-2 text-xs text-tenue">
        <tr>
          <th className="px-3 py-2 font-medium">Tipo</th>
          <th className="px-3 py-2 font-medium">Nombre (host)</th>
          <th className="px-3 py-2 font-medium">Destino (valor)</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="px-3 py-2 font-mono">{tipo}</td>
          <td className="px-3 py-2 font-mono">
            <span className="inline-flex items-center gap-1">
              {nombre}
              <Copiar texto={nombre} />
            </span>
          </td>
          <td className="px-3 py-2 font-mono">
            <span className="inline-flex items-center gap-1 break-all">
              {valor}
              <Copiar texto={valor} />
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
)

const Paso = ({ n, children }: { children: ReactNode; n: number }) => (
  <li className="flex gap-2.5">
    <span aria-hidden className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-violeta-suave text-[11px] font-semibold text-violeta">
      {n}
    </span>
    <div className="min-w-0 flex-1">{children}</div>
  </li>
)

/** Lo que hay que poner en el proveedor del dominio, en llano y con los valores exactos. */
const Pasos = ({ d, enLista, ocupado, anadirWww }: { anadirWww: (www: string) => void; d: DominioDeLaWeb; enLista: string[]; ocupado: null | string }) => {
  const dondeSeHace = 'Entra en la web donde compraste el dominio (GoDaddy, Namecheap, Hostinger, Punto.pe, Cloudflare…) y busca «DNS», «Zona DNS» o «Administrar DNS».'
  if (!d.raiz) {
    return (
      <ol className="mt-3 flex flex-col gap-3 rounded-caja bg-papel-2 p-4 text-[13px] text-tinta-2">
        <Paso n={1}>{dondeSeHace}</Paso>
        <Paso n={2}>
          Añade un registro con estos datos y guárdalo:
          <Registro nombre={d.registro.nombre} tipo={d.registro.tipo} valor={d.registro.valor} />
        </Paso>
        <Paso n={3}>
          Si ya había un registro con el nombre <span className="font-mono">{d.registro.nombre}</span> (otro CNAME o un registro A), bórralo o cámbialo por este: solo puede haber uno. Si tu dominio está en Cloudflare, deja la nube en gris («Solo DNS»).
        </Paso>
      </ol>
    )
  }
  const www = d.www ?? `www.${d.dominio}`
  const tieneWww = enLista.includes(www)
  return (
    <div className="mt-3 flex flex-col gap-3 rounded-caja bg-papel-2 p-4 text-[13px] text-tinta-2">
      <p>
        <strong className="text-tinta">{d.dominio}</strong> es un dominio raíz (sin «www»). Muchos proveedores no dejan poner un CNAME en la raíz, así que hay dos caminos:
      </p>
      <ol className="flex flex-col gap-3">
        <Paso n={1}>
          <strong className="text-tinta">Si tu proveedor lo admite</strong> (Cloudflare lo hace solo; otros lo llaman «ALIAS», «ANAME» o «CNAME flattening»): {dondeSeHace.charAt(0).toLowerCase() + dondeSeHace.slice(1)} Crea este registro en la raíz:
          <Registro nombre="@" tipo="CNAME (o ALIAS)" valor={d.registro.valor} />
        </Paso>
        <Paso n={2}>
          <strong className="text-tinta">Si no lo admite</strong>, usa <span className="font-mono">{www}</span> como dirección de tu web: conéctala aquí y, en tu proveedor, activa la «redirección» o «reenvío de dominio» para que <span className="font-mono">{d.dominio}</span> lleve a{' '}
          <span className="font-mono">https://{www}</span>.
          {!tieneWww && (
            <div className="mt-2">
              <Boton cargando={ocupado === `www-${d.dominio}`} onClick={() => anadirWww(www)} pequeno>
                Añadir {www}
              </Boton>
            </div>
          )}
        </Paso>
      </ol>
      <p>Si ya había registros A o CNAME en la raíz que llevaban a tu web anterior, bórralos: si no, unas visitas irán a un sitio y otras al otro.</p>
    </div>
  )
}

const NuevoDominio = ({ anadir, aviso, ocupado }: { anadir: (dominio: string, conWww: boolean) => Promise<boolean>; aviso: ReactNode; ocupado: boolean }) => {
  const [dominio, setDominio] = useState('')
  const [conWww, setConWww] = useState(true)
  const limpio = dominio
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/[/?#].*$/, '')
  // «¿También www?» solo tiene sentido para lo que no empieza ya por www; si es un
  // subdominio, el núcleo lo ignora (solo añade el www de una raíz).
  const ofrecerWww = Boolean(limpio) && !limpio.startsWith('www.') && limpio.split('.').length >= 2
  const enviar = (e: FormEvent) => {
    e.preventDefault()
    if (!limpio) return
    void anadir(limpio, ofrecerWww && conWww).then((bien) => bien && setDominio(''))
  }
  return (
    <Tarjeta>
      <h2 className="mb-1 font-display text-base font-semibold">Conectar un dominio</h2>
      <p className="mb-3 text-[13px] text-tenue">El dominio tiene que ser tuyo: lo compras en cualquier proveedor y aquí solo lo conectas.</p>
      {aviso}
      <form className="flex flex-col gap-3" onSubmit={enviar}>
        <div className="flex flex-wrap gap-2">
          <label className="sr-only" htmlFor="dominio-nuevo">
            Dominio
          </label>
          <span className="flex min-w-64 flex-1 items-center">
            <Globe aria-hidden className="mr-2 size-4 shrink-0 text-tenue" />
            <input
              autoCapitalize="none"
              autoComplete="off"
              className={claseDeEntrada()}
              id="dominio-nuevo"
              inputMode="url"
              onChange={(e) => setDominio(e.target.value)}
              placeholder="www.tunegocio.com"
              spellCheck={false}
              value={dominio}
            />
          </span>
          <Boton cargando={ocupado} disabled={!limpio} tipo="primario" type="submit">
            Añadir
          </Boton>
        </div>
        {ofrecerWww && (
          <label className={clases('flex items-center gap-2 text-[13px] text-tinta-2')}>
            <input checked={conWww} className="size-4 accent-violeta" onChange={(e) => setConWww(e.target.checked)} type="checkbox" />
            Si es el dominio raíz, añadir también www.{limpio} (recomendado: funciona con cualquier proveedor)
          </label>
        )}
      </form>
    </Tarjeta>
  )
}
